/**
 * Tipos do formulário da campanha/cliente mostrado durante a chamada e no
 * detalhe do histórico. Compartilhado entre o backend (rota /api/campaign-form)
 * e o widget. Sem imports de Node.
 */

export type CampaignFormSource = 'campaign' | 'customer';

export interface CampaignFormField {
  /** Chave do valor: nome do campo em minúsculas (como a API v1 do Callix devolve). */
  key: string;
  /** Rótulo para exibição (nome original do formulário quando o layout está disponível). */
  label: string;
  value: string;
  /** Largura do campo no layout do Callix (ex.: "100%"), quando conhecida. */
  width?: string;
}

export interface CampaignFormResponse {
  source: CampaignFormSource;
  id: number;
  /** Valores por chave (nome do campo em minúsculas). */
  values: Record<string, string>;
  /** Campos em ordem de exibição: a do layout do Callix quando disponível, senão alfabética. */
  fields: CampaignFormField[];
  /** Linhas do formulário do Callix já com os valores, quando o layout foi obtido. */
  rows: CampaignFormField[][] | null;
  /** De onde vieram os valores: API JSON:API v1 (chave do servidor) ou endpoint da interface web (sessão). */
  valuesSource: 'callix-api-v1' | 'callix-web-form';
  layoutSource: 'form-rows' | 'none';
  /**
   * Valores vieram da interface web (chaves = códigos dos campos) sem o layout que
   * traduz código -> nome: rótulos ficam técnicos. Não é cacheado; o widget refaz a
   * busca em seguida.
   */
  partial?: boolean;
  fetchedAt: string;
}

/** Atributos técnicos que a API v1 devolve junto com os campos do formulário. */
export const INTERNAL_FORM_KEYS = new Set([
  'id',
  'created_at',
  'updated_at',
  'person_id',
  'customer_id',
  'campaign_id',
  'campaign_list_id',
  'campaign_contact_id',
]);

export function isInternalFormKey(key: string): boolean {
  return INTERNAL_FORM_KEYS.has(normalizeFormKey(key));
}

/** "Dívida Ativa " -> "dívida ativa" (mesma forma das chaves da API v1). */
export function normalizeFormKey(value: string): string {
  return String(value ?? '')
    .normalize('NFC')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Rótulo quando não há layout: "divida ativa" -> "DIVIDA ATIVA" (padrão dos formulários Callix). */
export function humanizeFormKey(key: string): string {
  return normalizeFormKey(key).toUpperCase();
}

/**
 * Procura um valor pelos nomes candidatos, NA ORDEM dada (o primeiro candidato com
 * valor preenchido vence), sem distinguir caixa nem acentos.
 */
export function findFormValue(
  form: Pick<CampaignFormResponse, 'values'> | null | undefined,
  candidates: readonly string[]
): string | undefined {
  if (!form?.values) {
    return undefined;
  }
  const byNormalizedKey = new Map<string, string>();
  for (const [key, value] of Object.entries(form.values)) {
    const normalized = stripAccents(normalizeFormKey(key));
    if (!byNormalizedKey.has(normalized) && value !== undefined && value !== null && String(value).trim() !== '') {
      byNormalizedKey.set(normalized, String(value));
    }
  }
  for (const candidate of candidates) {
    const value = byNormalizedKey.get(stripAccents(normalizeFormKey(candidate)));
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

export function stripAccents(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
